/**
 * Browser file download helpers — wraps the Blob + anchor-click dance
 * used by export buttons (matrix CSV, project bundle, all-bundle).
 *
 * Mirrors the pattern inlined in MatrixViewer; lifted out so anything
 * that wants to offer a download (bundles, future asset exports) can
 * reuse it. Filename-stamp helper too.
 */

/** Trigger a browser download. Browsers handle the actual save dialog
 *  / save-to-default-downloads behavior. */
export function downloadBlob(content: string | Blob, filename: string, mime: string): void {
  const blob = typeof content === 'string'
    ? new Blob([content], { type: `${mime};charset=utf-8` })
    : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Append so Firefox honors the click; remove + revoke after a beat
  // so Safari/Edge have time to start the download.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Compact, sortable, human-readable timestamp for filenames. */
export function timestampForFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

/** Sanitize a string so it's safe to use in a filename across
 *  Windows / Mac / Linux. Strips path separators, control chars, and
 *  the handful of characters Windows forbids. Trims and collapses
 *  spaces to underscores. */
export function safeFilename(raw: string, maxLen = 80): string {
  const cleaned = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f<>:"/\\|?*]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  return cleaned.slice(0, maxLen) || 'untitled';
}

/** Open a native file picker and resolve with the first selected file
 *  (or null if the user cancelled). The accept string is the standard
 *  HTML `accept` attribute value (".json" or "image/*" etc.). */
export function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const f = input.files?.[0] ?? null;
      document.body.removeChild(input);
      resolve(f);
    });
    // Cancel branch: most browsers don't fire 'cancel' reliably, but
    // we cover it where supported.
    input.addEventListener('cancel', () => {
      document.body.removeChild(input);
      resolve(null);
    });
    document.body.appendChild(input);
    input.click();
  });
}

/** Read a File as a UTF-8 text string. */
export function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsText(file);
  });
}

/** Read a File as raw bytes — for binary formats like .tosc (zlib-
 *  compressed XML) that mustn't be UTF-8-decoded on the way in. */
export function readFileBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buf = reader.result;
      if (buf instanceof ArrayBuffer) resolve(new Uint8Array(buf));
      else reject(new Error('FileReader returned non-buffer result'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsArrayBuffer(file);
  });
}
